import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, ArrowRight, FileSpreadsheet, FileText, Clock, ShieldCheck, Infinity as InfinityIcon, Video, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading } from "@/components/shared";
import { NewsletterInline } from "@/components/Newsletter";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FOUNDER = "https://customer-assets-4nw71qhi.emergentagent.net/job_expertise-packager/artifacts/j4jgsiyb_IMG_6567.webp";
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FOR = [
  "You sell a physical product — you make it, brand it, distribute it or trade it",
  "Marketplace, D2C, distribution, modern trade, retail, manufacturing or export",
  "Owners, founders and sales heads who carry a number",
  "You're past 'does anyone want this' and into 'how do I move more of it, profitably'",
];
const NOT_FOR = [
  "You sell software or SaaS",
  "You run an agency or sell services by the hour",
  "You're a coach or creator selling courses",
  "You want motivation, not mechanics",
];

const WHAT_YOU_GET = [
  { icon: Video, title: "Recorded video course", desc: "Watch at your pace, rewatch any module whenever a deal needs it." },
  { icon: InfinityIcon, title: "Lifetime access", desc: "Buy once. It's yours — including every module still being recorded." },
  { icon: Download, title: "Every template", desc: "The working files — calculators, scripts, letters and trackers — not just slides." },
  { icon: ShieldCheck, title: "All future updates", desc: "New modules and templates land in your account at no extra cost." },
];

const FAQ = [
  { q: "Is this for me if I sell on Amazon or Flipkart?", a: "Yes. Marketplace sellers are exactly who this is for — pricing, protecting your listing rank, negotiating commission and ad spend, and the reorder are all covered." },
  { q: "I don't have a sales team yet. Useful?", a: "Very. Module 1 (the math) and modules 2–4 (channels, first conversation, pricing) are what you use before you hire anyone. Module 8 is for when you're ready to hire." },
  { q: "Are the templates actual files or just screenshots?", a: "Actual working files — Excel calculators, Word letters and PDFs you can use in your own business. They unlock the moment you buy." },
  { q: "What if it's not for me?", a: "14-day refund, no questions asked. Email and it's done." },
  { q: "Is it a live cohort?", a: "No. It's a recorded course you own for life, so you can watch and apply on your own schedule." },
];

function TemplateIcon({ type }) {
  const t = (type || "").toUpperCase();
  if (t === "XLSX") return <FileSpreadsheet className="h-4 w-4 text-emerald" />;
  return <FileText className="h-4 w-4 text-electric" />;
}

export default function SalesMastery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const { data: info, isLoading, refetch } = useQuery({ queryKey: ["sales-mastery-info"], queryFn: async () => (await api.get("/sales-mastery/info")).data });

  const buy = async () => {
    if (!user) { toast("Create a free account to buy — it's how we unlock your course."); navigate("/login"); return; }
    if (info?.owned) { navigate(`/courses/${info.course_slug}`); return; }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment window"); setPaying(false); return; }
      const { data: order } = await api.post("/sales-mastery/order");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: "Ultimate Sales Mastery", description: "Lifetime access", order_id: order.order_id,
        prefill: { name: order.name, email: order.email },
        theme: { color: "#059669" },
        handler: async (res) => {
          try {
            await api.post("/sales-mastery/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("You're in — welcome to Ultimate Sales Mastery.");
            refetch();
            navigate(`/courses/${info.course_slug}`);
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payments are opening soon — join the newsletter to be first to know.");
      setPaying(false);
    }
  };

  if (isLoading) return <PublicShell><Loading /></PublicShell>;

  const owned = info?.owned;
  const priceLabel = owned ? "You own this" : inr(info?.price);
  const cta = owned ? "Go to your course" : (info?.launch_active ? `Pre-order — ${inr(info?.price)}` : `Get lifetime access — ${inr(info?.price)}`);
  const modules = info?.modules || [];
  const templatesByModule = (info?.templates || []).reduce((acc, t) => {
    (acc[t.module] = acc[t.module] || []).push(t); return acc;
  }, {});

  const CTAButton = ({ testid }) => (
    <Button onClick={buy} disabled={paying} className="h-12 rounded-md bg-emerald px-8 text-base font-semibold text-white hover:bg-emerald-700" data-testid={testid}>
      {paying ? "…" : cta} {!paying && <ArrowRight className="ml-2 h-5 w-5" />}
    </Button>
  );

  return (
    <PublicShell>
      {/* 1. Hero */}
      <section className="relative overflow-hidden bg-navy grain">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navysurface/90" />
        <div className="relative mx-auto max-w-4xl px-5 py-24 lg:px-8 lg:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald/40 bg-emerald/10 px-4 py-1.5 text-xs font-semibold text-emerald">Ultimate Sales Mastery</span>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
            Everyone teaches you how to close a deal. Nobody teaches you how to sell a product that has to move off a shelf.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
            A recorded course plus every template you need to sell a physical product in India — priced, channelled, negotiated and reordered.
          </p>
          <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <CTAButton testid="hero-buy-btn" />
            <div className="text-sm text-slate-400">
              {owned ? "Lifetime access unlocked" : (
                <>
                  <span className="font-semibold text-white">{priceLabel}</span>
                  {info?.launch_active && <span className="ml-2">launch price · {info?.seats_remaining} of {info?.launch_cap} seats left</span>}
                  {!info?.launch_active && <span className="ml-2">lifetime access</span>}
                </>
              )}
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-500">14-day refund, no questions. {info?.launch_active ? `Launch price closes at ${info?.launch_cap} buyers, then it's ${inr(info?.standard_price)}.` : ""}</p>
        </div>
      </section>

      {/* 2. The problem */}
      <section className="mx-auto max-w-3xl px-5 py-20 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">The problem</p>
        <h2 className="mt-3 font-heading text-3xl font-bold text-navy sm:text-4xl">Every sales course was written for someone selling software.</h2>
        <div className="mt-5 space-y-4 text-lg leading-relaxed text-slate-700">
          <p>Close the deal. Handle the objection. Follow up. It all assumes the sale ends at "yes".</p>
          <p>When you sell a product, "yes" is the middle, not the end. The sale ends when the goods sell through — when the distributor reorders, the retailer keeps your facing, the marketplace rank holds. Everything between the first order and the reorder is where product businesses actually win or die, and nobody teaches it.</p>
          <p className="font-semibold text-navy">This course teaches that part.</p>
        </div>
      </section>

      {/* 3. Who it's for / isn't */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 lg:grid-cols-2 lg:px-8">
          <div className="rounded-2xl border border-emerald/30 bg-emerald/[0.04] p-8" data-testid="who-for">
            <h3 className="font-heading text-xl font-bold text-navy">This is for you if</h3>
            <ul className="mt-5 space-y-3">
              {FOR.map((t) => <li key={t} className="flex items-start gap-3 text-slate-700"><Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald" /> <span>{t}</span></li>)}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8" data-testid="who-not-for">
            <h3 className="font-heading text-xl font-bold text-navy">This isn't for you if</h3>
            <ul className="mt-5 space-y-3">
              {NOT_FOR.map((t) => <li key={t} className="flex items-start gap-3 text-slate-500"><X className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-400" /> <span>{t}</span></li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 4. The 8 modules with templates */}
      <section className="mx-auto max-w-5xl px-5 py-20 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">The curriculum</p>
        <h2 className="mt-3 font-heading text-3xl font-bold text-navy sm:text-4xl">Eight modules. Every one ships with the templates.</h2>
        <p className="mt-3 max-w-2xl text-slate-600">The templates are the reason to buy. Each module comes with the working files you use in your own business — not a summary of them.</p>
        <div className="mt-10 space-y-4">
          {modules.map((m, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-7" data-testid={`module-card-${i + 1}`}>
              <div className="flex items-start gap-4">
                <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-navy font-heading text-sm font-bold text-white">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-lg font-bold text-navy">{m.title}</h3>
                    {m.coming_soon
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700"><Clock className="h-3 w-3" /> Coming soon{m.expected_date ? ` · ${m.expected_date}` : ""}</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2.5 py-0.5 text-[11px] font-bold text-emerald"><Check className="h-3 w-3" /> Ready now</span>}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(templatesByModule[i + 1] || []).map((t) => (
                      <span key={t.id} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-navy" data-testid={`module-template-${t.id}`}>
                        <TemplateIcon type={t.file_type} /> {t.name} <span className="text-[10px] font-medium text-slate-400">{t.file_type}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. What you get */}
      <section className="bg-navy py-20 grain">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">What you get</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_YOU_GET.map((w) => (
              <div key={w.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid={`get-${w.title.split(" ")[0].toLowerCase()}`}>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-emerald/15 text-emerald"><w.icon className="h-5 w-5" strokeWidth={1.75} /></div>
                <h3 className="mt-4 font-heading text-base font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Who's teaching it */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div className="relative order-2 mx-auto w-full max-w-xs lg:order-1">
            <img src={FOUNDER} alt="Shubham Bapna" className="w-full rounded-2xl object-cover" />
            <div className="absolute -bottom-4 -right-2 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
              <p className="font-heading text-xl font-bold text-emerald">40 Under 40</p>
              <p className="text-xs text-slate-500">GJEPC · IIJS 2023</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">Who's teaching it</p>
            <h2 className="mt-3 font-heading text-3xl font-bold text-navy">Shubham Bapna</h2>
            <p className="mt-4 leading-relaxed text-slate-700">
              A second-generation manufacturer and exporter, 45-year family business behind him. He built and runs a consumer products brand sold across Amazon, Flipkart, quick commerce and his own retail floor in Mumbai — and is recognised by GJEPC as one of their 40 Under 40. This is the sales system he actually runs — the math, the channels, the negotiations and the reorder — taught the way an operator would explain it to another operator.
            </p>
            <Button onClick={() => navigate("/about")} variant="outline" className="mt-8 rounded-md" data-testid="sm-about-btn">More about Shubham</Button>
          </div>
        </div>
      </section>

      {/* 7. Honest pre-launch note */}
      <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-7" data-testid="prelaunch-note">
          <h3 className="font-heading text-lg font-bold text-navy">An honest note on timing</h3>
          <p className="mt-3 leading-relaxed text-slate-700">
            Three modules are recorded and ready today — <strong>The Sales Math</strong>, <strong>Pricing and Price Increases</strong>, and <strong>Building a Sales Team That Doesn't Lie to You</strong>. The remaining five are being recorded and will roll out over roughly six weeks, each with its templates. You buy once, at today's price, and every module lands in your account free as it's released. That's why the pre-order price is lower — you're early.
          </p>
        </div>
      </section>

      {/* 8. Price + refund + CTA */}
      <section className="mx-auto max-w-3xl px-5 pb-20 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm lg:p-12" data-testid="price-block">
          {info?.launch_active && !owned && (
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald/10 px-4 py-1.5 text-xs font-bold text-emerald">Pre-order · {info?.seats_remaining} of {info?.launch_cap} seats left</span>
          )}
          <div className="mt-4 flex items-end justify-center gap-3">
            <p className="font-heading text-5xl font-bold text-navy">{owned ? "Yours" : inr(info?.price)}</p>
            {info?.launch_active && !owned && <p className="mb-2 text-lg text-slate-400 line-through">{inr(info?.standard_price)}</p>}
          </div>
          <p className="mt-2 text-slate-600">Lifetime access · all templates · all future updates</p>
          <div className="mt-7 flex justify-center"><CTAButton testid="price-buy-btn" /></div>
          <p className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald" /> 14-day refund, no questions asked</p>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-24 lg:px-8">
        <h2 className="font-heading text-3xl font-bold text-navy">Questions</h2>
        <div className="mt-8 divide-y divide-slate-200 border-t border-slate-200">
          {FAQ.map((f, i) => (
            <div key={i} className="py-6" data-testid={`faq-${i}`}>
              <h3 className="font-heading text-lg font-semibold text-navy">{f.q}</h3>
              <p className="mt-2 leading-relaxed text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="sm-newsletter">
          <p className="font-heading text-base font-semibold text-navy">Not ready to buy? Get the free daily story.</p>
          <p className="mt-1 text-sm text-slate-600">One real business story every morning — the same thinking this course is built on.</p>
          <div className="mt-4"><NewsletterInline source="unknown" compact /></div>
        </div>
        <p className="mt-8 text-center text-sm text-slate-500" data-testid="sm-library-link">
          Want the cheap entry point?{" "}
          <button onClick={() => navigate("/library")} className="font-semibold text-emerald underline-offset-4 hover:underline">The Library</button>
          {" "}— nine complete programs, 63 resources, one payment of ₹499.
        </p>
      </section>
    </PublicShell>
  );
}
